/*

  Challengelist.ts

  List of challenges (received and sent) display component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, ChallengeListItem } from "./types";
import { ts, t, mt } from "./i18n";
import { Component, ComponentFunc, m } from "./mithril";
import { glyph, glyphGrayed } from "./util";
import { UserInfoButton } from "./components";
import { logEvent } from "./channel";

interface IChallengeListEntryAttrs {
  view: IView;
  item: ChallengeListItem;
  i: number;
}

const ChallengeListEntry: ComponentFunc<IChallengeListEntryAttrs> = (initialVnode) => {
  // Generate a list item about a pending challenge (issued or received)

  const { view, item, i } = initialVnode.attrs;
  const { model } = view;

  function challengeDescription(json: { duration?: number; }) {
    /* Return a human-readable string describing a challenge
    according to the enclosed preferences */
    if (!json || json.duration === undefined || json.duration === 0)
      /* Normal unbounded (untimed) game */
      return t("Venjuleg ótímabundin viðureign");
    return t("with_clock", { duration: json.duration.toString() });
  }

  function markChallenge(ev: Event) {
    // Clicked the icon at the beginning of the line,
    // to decline a received challenge or retract an issued challenge
    ev.preventDefault();
    if (item.received) {
      view.actions.declineChallenge(item.userid, item.key);
    } else {
      view.actions.retractChallenge(item.userid, item.key);
    }
  }

  function clickChallenge(ev: Event) {
    // Clicked the hotspot area to accept a received challenge
    ev.preventDefault();
    if (!model.moreGamesAllowed()) {
      // User must be a friend to be able to accept more challenges
      const state = model.state;
      if (state) {
        logEvent("hit_game_limit",
          {
            userid: state.netskraflUserId,
            locale: state.locale,
            limit: model.maxFreeGames
          }
        );
      }
      // Promote a subscription to Netskrafl/Explo
      view.showFriendPromo();
      return;
    }
    if (item.received) {
      if (item.prefs && item.prefs.duration !== undefined && item.prefs.duration > 0)
        // Timed game: display a modal wait dialog
        view.pushDialog("wait", {
          oppId: item.userid,
          oppNick: item.opp,
          oppName: item.fullname,
          duration: item.prefs.duration,
          challengeKey: item.key,
        });
      else
        // Ask the server to create a new game and route to it
        view.actions.startNewGame(item.userid, false);
    }
    else {
      // Clicking on a sent challenge, i.e. a timed game
      // where the opponent is waiting and ready to start
      view.showAcceptDialog(item.userid, item.opp, item.key);
    }
  }

  return {
    view: () => {

      const oppReady = !item.received && item.opp_ready &&
        item.prefs && item.prefs.duration !== undefined &&
        item.prefs.duration > 0;
      const clickable = item.received || oppReady;
      const descr = challengeDescription(item.prefs);

      return m(".listitem" + (i % 2 === 0 ? ".oddlist" : ".evenlist"),
        [
          m("span.list-ch",
            { onclick: markChallenge },
            item.received ?
              glyph("thumbs-down", { title: ts("Hafna") })
              :
              glyph("hand-right", { title: ts("Afturkalla") })
          ),
          m(clickable ? "a.flex" : "span.flex",
            clickable ? {
              href: "#",
              onclick: clickChallenge,
              class: oppReady ? "opp-ready" : ""
            } : {},
            [
              m("span.list-ts", item.ts),
              m("span.list-nick", { title: item.fullname }, item.opp),
              m("span.list-chall",
                [
                  item.prefs.fairplay ? m("span.fairplay-btn", { title: ts("Án hjálpartækja") }) : "",
                  item.prefs.manual ? m("span.manual-btn", { title: ts("Keppnishamur") }) : "",
                  descr
                ]
              )
            ]
          ),
          m(UserInfoButton,
            {
              view,
              item: {
                userid: item.userid,
                nick: item.opp,
                fullname: item.fullname,
              }
            }
          ),
        ]
      );
    }
  };
};

interface IAttributes {
  view: IView;
  showReceived: boolean;
}

export const ChallengeList: Component<IAttributes> = {
  // Shows a list of challenges (received or sent), obtained from view.model.challengeList

  view: (vnode) => {
    const { view, showReceived } = vnode.attrs;
    const { model } = view;

    if (!model.state) return [];

    // Trigger loading of challenge list if needed
    if (model.challengeList === null)
      model.loadChallengeList();

    let cList: ChallengeListItem[] = [];
    if (model.challengeList)
      cList = showReceived ?
        model.challengeList.filter((item) => item.received) :
        model.challengeList.filter((item) => !item.received);

    const listContent = m("div",
      { id: showReceived ? 'chall-received' : 'chall-sent' },
      cList.map((item, i) => m(ChallengeListEntry, { view, item, i }))
    );

    if (showReceived)
      // Challenges received
      return [
        m(".listitem.listheader",
          [
            m("span.list-icon", glyphGrayed("thumbs-down", { title: ts('Hafna') })),
            mt("span.list-ts", "Hvenær"),
            mt("span.list-nick", "Áskorandi"),
            mt("span.list-chall", "Hvernig"),
            mt("span.list-info-hdr", "Ferill"),
          ]
        ),
        listContent
      ];
    else
      // Challenges sent
      return [
        m(".listitem.listheader",
          [
            m("span.list-icon", glyphGrayed("hand-right", { title: ts('Afturkalla') })),
            mt("span.list-ts", "Hvenær"),
            mt("span.list-nick", "Andstæðingur"),
            mt("span.list-chall", "Hvernig"),
            mt("span.list-info-hdr", "Ferill"),
          ]
        ),
        listContent
      ];
  }
};
